import React, { useState, useEffect } from 'react';
import { Target, ScrollText, Leaf, Droplets, HeartPulse, Home, GitFork, Zap, Users, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { t, getCurrentLanguage } from '../utils/translations';
import api from '../components/api';

const AboutPage = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Écouter les changements de langue
  useEffect(() => {
    // Détecter la langue depuis l'URL au chargement initial
    const detectLanguageFromURL = () => {
      const path = window.location.pathname;
      if (path.startsWith('/en/')) {
        return 'en';
      } else if (path.startsWith('/fr/')) {
        return 'fr';
      }
      return 'fr'; // Par défaut
    };

    const initialLang = detectLanguageFromURL();
    setCurrentLanguage(initialLang);
    
    // Forcer la mise à jour de i18n si nécessaire
    if (window.i18n && window.i18n.language !== initialLang) {
      window.i18n.changeLanguage(initialLang);
      localStorage.setItem('i18nextLng', initialLang);
    }

    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);

    // Écouter aussi les changements i18n
    const i18nListener = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    if (window.i18n) {
      window.i18n.on('languageChanged', i18nListener);
    }

    // Écouter les changements d'URL
    const handleURLChange = () => {
      const urlLang = detectLanguageFromURL();
      if (urlLang !== currentLanguage) {
        setCurrentLanguage(urlLang);
        if (window.i18n && window.i18n.language !== urlLang) {
          window.i18n.changeLanguage(urlLang);
        }
      }
    };

    window.addEventListener('popstate', handleURLChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('popstate', handleURLChange);
      if (window.i18n) {
        window.i18n.off('languageChanged', i18nListener);
      }
    };
  }, [currentLanguage]);

  // Récupérer les données de la page About
  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/about-page/');
        setAboutData(response.data);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement de la page');
        console.error('Erreur API About:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  // Fonction pour obtenir le contenu traduit
  const getTranslatedContent = (frContent, enContent) => {
    if (currentLanguage === 'en' && enContent) {
      return enContent;
    }
    return frContent || enContent || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#089556] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !aboutData) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex-grow flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Erreur de chargement</h2>
            <p className="text-gray-600 mb-6">{error || 'Aucune donnée disponible'}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#089556] text-white px-6 py-3 rounded-lg hover:bg-[#078a4a] transition-colors duration-200"
            >
              Réessayer
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[400px] mb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#0b6f47] to-[#07D1EB]">
          <div className="absolute inset-0 opacity-[0.3]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 3px, transparent 3px),
                radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2.5px, transparent 2.5px),
                radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 2px, transparent 2px),
                radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px),
                radial-gradient(circle at 90% 50%, rgba(255,255,255,0.8) 2.8px, transparent 2.8px),
                radial-gradient(circle at 30% 60%, rgba(255,255,255,0.6) 2.2px, transparent 2.2px)
              `,
              backgroundSize: '200px 200px, 160px 160px, 220px 220px, 180px 180px, 190px 190px, 150px 150px'
            }}></div>
          </div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8 shadow-xl border border-white/30">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight drop-shadow-lg mb-4">
                {getTranslatedContent(aboutData.hero_title_fr, aboutData.hero_title_en)}
              </h1>
              <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-sm">
                {getTranslatedContent(aboutData.hero_subtitle_fr, aboutData.hero_subtitle_en)}
              </p>
              <div className="w-32 h-1 bg-white/60 mx-auto mt-6 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Avant-propos Section */}
        <section className="relative mb-20 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.08]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, #089556 2px, transparent 2px),
                radial-gradient(circle at 75% 75%, #07D1EB 1.5px, transparent 1.5px),
                radial-gradient(circle at 50% 10%, #089556 1px, transparent 1px)
              `,
              backgroundSize: '150px 150px, 120px 120px, 180px 180px'
            }}></div>
          </div>

          <div className="relative z-10 bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
            <div className="flex items-center gap-6 mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <ScrollText className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(aboutData.preface_title_fr, aboutData.preface_title_en)}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            <div className="grid md:grid-cols-1 gap-8 text-gray-700">
              <div className="space-y-6 text-lg leading-relaxed">
                <div 
                  className="text-justify"
                  dangerouslySetInnerHTML={{
                    __html: getTranslatedContent(aboutData.preface_content_fr, aboutData.preface_content_en)
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="relative mb-20 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 80% 20%, #07D1EB 2px, transparent 2px),
                radial-gradient(circle at 20% 80%, #089556 1.5px, transparent 1.5px)
              `,
              backgroundSize: '160px 160px, 140px 140px'
            }}></div>
          </div>

          <div className="relative z-10 bg-gradient-to-br from-[#089556]/10 via-[#089556]/5 to-[#07D1EB]/10 p-8 rounded-2xl shadow-lg border border-white/20">
            <div className="flex items-center gap-6 mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <Target className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(aboutData.vision_title_fr, aboutData.vision_title_en)}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700">
              <div 
                className="text-lg leading-relaxed text-justify"
                dangerouslySetInnerHTML={{
                  __html: getTranslatedContent(aboutData.vision_content_fr, aboutData.vision_content_en)
                }}
              />
            </div>
          </div>
        </section>

        {/* Plans stratégiques Section */}
        <section className="relative mb-20 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.05]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 40% 60%, #089556 2px, transparent 2px),
                radial-gradient(circle at 60% 40%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '180px 180px, 150px 150px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-6 mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <GitFork className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(aboutData.strategy_title_fr, aboutData.strategy_title_en)}
                </h2>
                <p className="text-xl mt-2 text-gray-600">
                  {getTranslatedContent(aboutData.strategy_subtitle_fr, aboutData.strategy_subtitle_en)}
                </p>
                <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>

            {/* Vision et Objectifs */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl mb-8 p-8 border border-white/20">
              <h3 className="text-2xl font-semibold text-[#089556] mb-4 flex items-center gap-3">
                <CheckCircle className="h-6 w-6" />
                {getTranslatedContent(aboutData.strategy_vision_title_fr, aboutData.strategy_vision_title_en)}
              </h3>
              <div 
                className="text-gray-700 mb-6 text-lg leading-relaxed text-justify"
                dangerouslySetInnerHTML={{
                  __html: getTranslatedContent(aboutData.strategy_vision_content_fr, aboutData.strategy_vision_content_en)
                }}
              />
            </div>

            {/* Axes Stratégiques */}
            {aboutData.strategy_axes && aboutData.strategy_axes.length > 0 && (
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {aboutData.strategy_axes.map((axe, index) => (
                  <div key={index} className="group relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20 hover:shadow-xl transition-all duration-300">
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${axe.gradient || 'from-blue-500 to-purple-600'} rounded-t-2xl`}></div>
                    
                    <h4 className="text-xl font-semibold text-[#089556] mb-4 mt-2">
                      {getTranslatedContent(axe.title_fr, axe.title_en)}
                    </h4>
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl">
                        <p className="font-medium text-gray-700 flex items-center gap-2">
                          <Target className="h-4 w-4 text-[#089556]" />
                          Objectif :
                        </p>
                        <p className="text-gray-600 mt-1 text-justify">
                          {getTranslatedContent(axe.objectif_fr, axe.objectif_en)}
                        </p>
                      </div>
                      {axe.actions_fr && axe.actions_fr.length > 0 && (
                        <div>
                          <p className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-[#089556]" />
                            Actions :
                          </p>
                          <ul className="space-y-2">
                            {getTranslatedContent(axe.actions_fr, axe.actions_en).map((action, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-gray-600">
                                <span className="text-[#07D1EB] mt-1">•</span>
                                <span className="text-justify">{action}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Objectifs Section */}
        <section className="relative mb-20 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 30% 70%, #089556 2px, transparent 2px),
                radial-gradient(circle at 70% 30%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '170px 170px, 140px 140px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-6 mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <Target className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(aboutData.objectives_title_fr, aboutData.objectives_title_en)}
                </h2>
                <p className="text-xl mt-2 text-gray-600">
                  {getTranslatedContent(aboutData.objectives_subtitle_fr, aboutData.objectives_subtitle_en)}
                </p>
                <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            
            {aboutData.objectives_items && aboutData.objectives_items.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aboutData.objectives_items.map((objective, index) => (
                  <div key={index} className="group relative bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${objective.gradient || 'from-blue-500 to-purple-600'} rounded-t-2xl`}></div>
                    
                    <div className="text-[#089556] mb-4 mt-2">
                      {objective.icon === 'Home' && <Home className="h-8 w-8" />}
                      {objective.icon === 'ScrollText' && <ScrollText className="h-8 w-8" />}
                      {objective.icon === 'Droplets' && <Droplets className="h-8 w-8" />}
                      {objective.icon === 'HeartPulse' && <HeartPulse className="h-8 w-8" />}
                      {objective.icon === 'Leaf' && <Leaf className="h-8 w-8" />}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {getTranslatedContent(objective.title_fr, objective.title_en)}
                    </h3>
                    {objective.points_fr && objective.points_fr.length > 0 && (
                      <ul className="space-y-2 text-gray-600">
                        {getTranslatedContent(objective.points_fr, objective.points_en).map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-[#07D1EB] mr-2">•</span>
                            <span className="text-justify">{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Approches Section */}
        <section className="relative mb-20 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.05]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 60% 40%, #089556 2px, transparent 2px),
                radial-gradient(circle at 40% 60%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '160px 160px, 130px 130px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-6 mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <GitFork className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(aboutData.approaches_title_fr, aboutData.approaches_title_en)}
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Axes du Changement */}
              <div className="bg-gradient-to-br from-[#089556]/10 via-[#089556]/5 to-[#07D1EB]/10 p-8 rounded-2xl shadow-lg border border-white/20">
                <h3 className="text-2xl font-semibold text-[#089556] mb-6 flex items-center gap-3">
                  <Zap className="h-6 w-6" />
                  {getTranslatedContent(aboutData.change_axes_title_fr, aboutData.change_axes_title_en)}
                </h3>
                {aboutData.change_axes_items_fr && aboutData.change_axes_items_fr.length > 0 && (
                  <ul className="space-y-4">
                    {getTranslatedContent(aboutData.change_axes_items_fr, aboutData.change_axes_items_en).map((item, i) => (
                      <li key={i} className="flex items-center gap-4 text-gray-700">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-r from-[#089556] to-[#07D1EB] flex items-center justify-center text-white font-bold shadow-lg">
                          {i + 1}
                        </div>
                        <span className="text-justify">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Catalyseurs */}
              <div className="bg-gradient-to-br from-[#07D1EB]/10 via-[#07D1EB]/5 to-[#089556]/10 p-8 rounded-2xl shadow-lg border border-white/20">
                <h3 className="text-2xl font-semibold text-[#089556] mb-6 flex items-center gap-3">
                  <Users className="h-6 w-6" />
                  {getTranslatedContent(aboutData.catalysts_title_fr, aboutData.catalysts_title_en)}
                </h3>
                {aboutData.catalysts_items_fr && aboutData.catalysts_items_fr.length > 0 && (
                  <ul className="space-y-4">
                    {getTranslatedContent(aboutData.catalysts_items_fr, aboutData.catalysts_items_en).map((item, i) => (
                      <li key={i} className="flex items-center gap-4 text-gray-700">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-r from-[#07D1EB] to-[#089556] flex items-center justify-center text-white font-bold shadow-lg">
                          {i + 1}
                        </div>
                        <span className="text-justify">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
